import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, AlertCircle, RefreshCw, Undo2, Package, Truck, CheckCircle2, Upload, X, Star } from 'lucide-react';

const OrderHistoryPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal Interactions State Config (Returns & Exchanges)
  const [activeOrderId, setActiveOrderId] = useState(null);
  const [requestType, setRequestType] = useState('Return');
  const [selectedReason, setSelectedReason] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);

  // Integrated Review Workspace State Matrix
  const [activeReviewKey, setActiveReviewKey] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHoverRating, setReviewHoverRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewMedia, setReviewMedia] = useState([]);

  useEffect(() => {
    const loadUserHistory = async () => {
      try {
        const token = localStorage.getItem('modische_token') || localStorage.getItem('token');
        if (!token) {
          navigate('/auth');
          return;
        }

        const response = await fetch('https://api.modischekledings.com/api/orders/my-orders', {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        if (data.success) {
          setOrders(data.orders || []);
        }
      } catch (err) {
        console.error("Error reading order registers:", err);
      } finally {
        setLoading(false);
      }
    };
    loadUserHistory();
  }, [navigate]);

  const isEligibleForPolicyWindow = (createdAtDate) => {
    if (!createdAtDate) return false;
    const orderDate = new Date(createdAtDate);
    const currentDate = new Date();
    const discrepancyTime = currentDate.getTime() - orderDate.getTime();
    const elapsedDays = discrepancyTime / (1000 * 3600 * 24);
    return elapsedDays <= 3; 
  };

  const parseTrackingDetails = (order) => {
    if (!order) return null;
    
    // Direct fields if set by Shiprocket webhook
    if (order.shiprocketAwb || order.trackingNumber) {
      return {
        awb: order.shiprocketAwb || order.trackingNumber,
        provider: order.courierName || 'Shiprocket Partner'
      };
    }

    // String fallback if parsed inside returnReason or notes
    const trackingString = order.returnReason || '';
    if (trackingString.includes('AWB:')) {
      try {
        const awbPart = trackingString.split('AWB:')[1]?.split('|')[0]?.trim() || '';
        const providerPart = trackingString.split('(')[1]?.split(')')[0] || 'Shiprocket';
        return { awb: awbPart, provider: providerPart };
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const handleImageUploadChange = (e) => {
    const files = Array.from(e.target.files);
    if (uploadedImages.length + files.length > 6) {
      return alert("🔒 Policy Limitation: You can upload a maximum of 6 verification images of the garment.");
    }
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImages(prev => [...prev, reader.result].slice(0, 6));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeUploadedImage = (indexToRemove) => {
    setUploadedImages(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleReviewMediaChange = (e) => {
    const files = Array.from(e.target.files);
    if (reviewMedia.length + files.length > 3) {
      return alert("🔒 Review Limitation: You can attach up to 3 media file items (Photos or Videos).");
    }
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setReviewMedia(prev => [...prev, { data: reader.result, type: file.type }].slice(0, 3));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeReviewMediaItem = (idxToRemove) => {
    setReviewMedia(prev => prev.filter((_, idx) => idx !== idxToRemove));
  };

  const handlePolicyActionSubmit = async () => {
    if (!selectedReason) return alert("Please select a valid policy reason modifier.");
    
    const requiresVideoProof = ["Wrong product delivered", "Damaged / defective item received", "Missing items or quantity mismatch"].includes(selectedReason);
    if (requiresVideoProof && uploadedImages.length === 0) {
      return alert("⚠️ Quality verification photo captures are required for this return condition category. Please attach garment images.");
    }

    try {
      const token = localStorage.getItem('modische_token') || localStorage.getItem('token');
      const response = await fetch(`https://api.modischekledings.com/api/orders/policy-action/${activeOrderId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          actionType: requestType, 
          reason: selectedReason,
          notes: customNotes,
          images: uploadedImages 
        })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        alert(`${requestType} request successfully filed. Support team will review your details within 24–48 hours.`);
        setOrders(prev => prev.map(o => o._id === activeOrderId ? { ...o, status: `${requestType} Requested` } : o));
        
        setActiveOrderId(null);
        setSelectedReason('');
        setCustomNotes('');
        setUploadedImages([]);
      } else {
        alert(data.message || "Action request validation failed.");
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Validation connectivity warning.");
    }
  };

  const handleReviewSubmit = async (orderId, productId) => {
    if (reviewRating === 0) return alert("Please select a star rating modifier before publishing.");
    
    try {
      const token = localStorage.getItem('modische_token') || localStorage.getItem('token');
      const response = await fetch('https://api.modischekledings.com/api/reviews', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          orderId,
          productId,
          rating: reviewRating,
          comment: reviewComment,
          media: reviewMedia
        })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        alert("Thank you! Your verified item review has been published.");
        setActiveReviewKey(null);
        setReviewRating(0);
        setReviewComment('');
        setReviewMedia([]);
      } else {
        alert(data.message || "Failed to submit item review.");
      }
    } catch (err) {
      console.error(err);
      alert("Connectivity warning encountered recording item reviews.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center font-mono text-xs uppercase tracking-widest text-[#664229]">
        Accessing Personal Archival Logs...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5DC] pt-36 pb-20 px-4 sm:px-6 lg:px-8 max-w-[1200px] mx-auto text-left font-sans">
      <button 
        onClick={() => navigate('/')} 
        className="flex items-center gap-1.5 text-[8px] uppercase tracking-widest text-[#664229] hover:text-[#2C1E12] transition font-black mb-8 bg-transparent border-none cursor-pointer"
      >
        <ArrowLeft size={10} /> Back To Showroom
      </button>

      <div className="border-b border-[#2C1E12]/10 pb-6 mb-12">
        <h2 className="text-3xl font-serif uppercase tracking-wider text-[#2C1E12]">Order History</h2>
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#664229] opacity-70 mt-1">Review verified asset purchase logs & policy timelines</p>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white p-12 border border-[#E5D3B3]/40 rounded-sm text-center">
          <p className="text-xs font-mono uppercase text-zinc-400 mb-4">No transactions recorded for this account portfolio.</p>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-2.5 bg-[#2C1E12] text-[#F5F5DC] text-[9px] uppercase tracking-widest font-black rounded-xs border-none cursor-pointer hover:opacity-90 transition"
          >
            Explore Catalogue
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {orders.map((order) => {
            const withinThreeDays = isEligibleForPolicyWindow(order.createdAt);
            const trackingData = parseTrackingDetails(order);
            
            const currentStatus = order.status || 'Processing';
            let currentStep = 0; 
            if (currentStatus === 'Shipped' || currentStatus === 'In Transit' || currentStatus === 'Dispatched') currentStep = 1;
            if (currentStatus === 'Delivered' || currentStatus.includes('Requested')) currentStep = 2;

            return (
              <div key={order._id} className="bg-white border border-[#E5D3B3]/40 p-6 rounded-sm shadow-sm flex flex-col transition-all hover:shadow-md w-full max-w-full overflow-hidden">
                
                {/* CARD HEADER */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-zinc-100 gap-3 mb-4 w-full">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-[9px] font-mono bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-xs font-bold uppercase">
                      ID: {order._id.slice(-8).toUpperCase()}
                    </span>
                    <span className="text-[9px] font-mono text-zinc-400 flex items-center gap-1">
                      <Clock size={10} /> {new Date(order.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-left sm:text-right text-xs">
                    <span className="text-zinc-400 mr-2">Total Amount Paid:</span>
                    <span className="text-sm font-serif font-black text-[#2C1E12]">₹{order.totalAmount}</span>
                  </div>
                </div>

                {/* GARMENT ITEM LIST */}
                <div className="divide-y divide-zinc-100 mb-6 w-full">
                  {order.items?.map((item, idx) => {
                    const productId = item.product?._id || item.product || item.id;
                    const productImage = item.product?.image || item.image || (Array.isArray(item.product?.images) ? item.product?.images[0] : null);
                    const isReviewOpen = activeReviewKey === `${order._id}-${productId}`;

                    return (
                      <div key={idx} className="py-4 flex flex-col w-full min-w-0">
                        <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-4 w-full min-w-0">
                          <div className="flex items-center gap-4 min-w-0 flex-1">
                            <div 
                              onClick={() => productId && navigate(`/product/${productId}`)} 
                              className="w-16 h-20 bg-zinc-50 border border-zinc-100 rounded-xs overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition"
                            >
                              <img 
                                src={productImage || 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?q=80&w=150'} 
                                alt={item.name || "Product"} 
                                className="w-full h-full object-cover" 
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 
                                onClick={() => productId && navigate(`/product/${productId}`)} 
                                className="text-xs font-serif font-black uppercase text-[#2C1E12] tracking-wide break-words cursor-pointer hover:underline inline-block max-w-full"
                              >
                                {item.name || item.product?.name || "Garment Item"}
                              </h4>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-[10px] font-mono text-zinc-500 uppercase">
                                <div>Size: <span className="font-bold text-[#2C1E12]">{item.size || 'OS'}</span></div>
                                <div>Qty: <span className="font-bold text-[#2C1E12]">{item.quantity || 1}</span></div>
                                <div>Price: <span className="font-sans font-bold text-[#2C1E12]">₹{item.price || 0}</span></div>
                              </div>
                            </div>
                          </div>

                          {order.status === 'Delivered' && (
                            <div className="w-full sm:w-auto flex justify-end">
                              <button 
                                onClick={() => {
                                  if (isReviewOpen) {
                                    setActiveReviewKey(null);
                                  } else {
                                    setActiveReviewKey(`${order._id}-${productId}`);
                                    setReviewRating(0);
                                    setReviewComment('');
                                    setReviewMedia([]);
                                  }
                                }}
                                className="text-[8px] font-black uppercase tracking-widest border border-[#2C1E12]/40 text-[#2C1E12] bg-white px-3 py-1.5 hover:bg-zinc-50 transition rounded-xs flex items-center gap-1 cursor-pointer"
                              >
                                <Star size={9} className="fill-current" /> {isReviewOpen ? 'Cancel Feedback' : 'Write Review'}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* REVIEW DRAWER */}
                        {isReviewOpen && (
                          <div className="bg-[#FAF9F6] border border-[#E5D3B3]/60 p-5 mt-4 text-left rounded-xs space-y-4 max-w-xl shadow-inner w-full">
                            <div>
                              <h5 className="text-[10px] uppercase font-bold tracking-[0.2em] text-[#2C1E12]">Share Your Experience</h5>
                              <p className="text-[8px] text-zinc-400 uppercase tracking-wide mt-0.5">Rate fabric quality, item detailing, and true fit</p>
                            </div>

                            <div className="flex items-center space-x-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  type="button"
                                  onClick={() => setReviewRating(star)}
                                  onMouseEnter={() => setReviewHoverRating(star)}
                                  onMouseLeave={() => setReviewHoverRating(0)}
                                  className="transition duration-300 focus:outline-none bg-transparent border-none p-0 cursor-pointer"
                                >
                                  <Star 
                                    size={16} 
                                    className={`transition-all duration-300 ${
                                      star <= (reviewHoverRating || reviewRating) ? 'text-[#664229] fill-[#664229]' : 'text-zinc-200'
                                    }`} 
                                  />
                                </button>
                              ))}
                              <span className="text-[8px] uppercase tracking-widest text-[#664229]/60 pl-2 font-mono">
                                {reviewRating > 0 ? `${reviewRating} / 5 Stars` : 'Rate Asset'}
                              </span>
                            </div>

                            <div className="space-y-1">
                              <label className="text-[8px] uppercase font-black tracking-widest text-[#664229] block">Comments</label>
                              <textarea
                                value={reviewComment}
                                onChange={(e) => setReviewComment(e.target.value)}
                                rows="3"
                                placeholder="Describe product texture weight, drape dimensions, execution notes..."
                                className="w-full bg-white border border-zinc-200 p-3 text-xs text-[#2C1E12] placeholder-zinc-300 focus:outline-none focus:border-[#2C1E12] rounded-xs font-sans resize-none"
                              />
                            </div>

                            <div className="space-y-2">
                              <label className="text-[8px] uppercase font-black tracking-widest text-[#664229] block">
                                Visual Attachments ({reviewMedia.length}/3 Optional Uploads)
                              </label>
                              <div className="flex flex-wrap items-center gap-3">
                                {reviewMedia.length < 3 && (
                                  <label className="border border-dashed border-zinc-300 hover:border-zinc-400 text-zinc-400 hover:text-zinc-600 px-4 py-3 text-[9px] uppercase tracking-wider cursor-pointer bg-white transition rounded-xs font-mono flex items-center gap-1">
                                    <Upload size={10} /> Add Photo/Video
                                    <input 
                                      type="file" 
                                      multiple 
                                      accept="image/*,video/*" 
                                      onChange={handleReviewMediaChange} 
                                      className="hidden" 
                                    />
                                  </label>
                                )}

                                {reviewMedia.map((mediaItem, mIdx) => (
                                  <div key={mIdx} className="w-12 h-16 bg-white border border-zinc-200 rounded-xs relative overflow-hidden group">
                                    {mediaItem.type.startsWith('image/') ? (
                                      <img src={mediaItem.data} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex flex-col items-center justify-center text-[7px] text-zinc-400 font-bold bg-zinc-50 uppercase p-0.5 text-center">🎥 Movie</div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => removeReviewMediaItem(mIdx)}
                                      className="absolute inset-0 bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition border-none cursor-pointer"
                                    >
                                      <X size={10} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={() => handleReviewSubmit(order._id, productId)}
                              className="w-full py-2.5 bg-[#2C1E12] text-[#F5F5DC] text-[9px] uppercase tracking-[0.25em] font-black hover:opacity-90 transition rounded-xs border-none cursor-pointer shadow-sm"
                            >
                              Publish Review
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* TRACKING TIMELINE */}
                <div className="bg-zinc-50/60 border border-zinc-100/80 p-5 rounded-sm mb-4 w-full">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between max-w-xl mx-auto relative mb-2 gap-4 sm:gap-2">
                    <div className="absolute left-[8%] right-[8%] top-3.5 h-[2px] bg-zinc-200 z-0 hidden sm:block">
                      <div className="h-full bg-[#2C1E12] transition-all duration-500" style={{ width: currentStep === 0 ? '0%' : currentStep === 1 ? '50%' : '100%' }} />
                    </div>
                    
                    <div className="flex flex-row sm:flex-col items-center z-10 bg-transparent gap-3 sm:gap-0 w-full sm:w-auto">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors border flex-shrink-0 ${currentStep >= 0 ? 'bg-[#2C1E12] border-[#2C1E12] text-[#F5F5DC]' : 'bg-white border-zinc-200 text-zinc-400'}`}>
                        <Package size={14} />
                      </div>
                      <span className="text-[9px] font-bold uppercase tracking-wider sm:mt-1 text-[#2C1E12]">Processing</span>
                    </div>
                    
                    <div className="flex flex-row sm:flex-col items-center z-10 bg-transparent gap-3 sm:gap-0 w-full sm:w-auto">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors border flex-shrink-0 ${currentStep >= 1 ? 'bg-[#2C1E12] border-[#2C1E12] text-[#F5F5DC]' : 'bg-white border-zinc-200 text-zinc-400'}`}>
                        <Truck size={14} />
                      </div>
                      <span className={`text-[9px] font-bold uppercase tracking-wider sm:mt-1 ${currentStep >= 1 ? 'text-[#2C1E12]' : 'text-zinc-400'}`}>Shipped</span>
                    </div>
                    
                    <div className="flex flex-row sm:flex-col items-center z-10 bg-transparent gap-3 sm:gap-0 w-full sm:w-auto">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors border flex-shrink-0 ${currentStep >= 2 ? 'bg-green-700 border-green-700 text-white' : 'bg-white border-zinc-200 text-zinc-400'}`}>
                        <CheckCircle2 size={14} />
                      </div>
                      <span className={`text-[9px] font-bold uppercase tracking-wider sm:mt-1 ${currentStep >= 2 ? 'text-green-700' : 'text-zinc-400'}`}>Delivered</span>
                    </div>
                  </div>

                  {trackingData && (
                    <div className="mt-4 pt-3 border-t border-zinc-200/60 flex flex-col sm:flex-row justify-between items-start sm:items-center text-[10px] font-mono text-zinc-500 uppercase gap-2 w-full">
                      <div>Carrier: <span className="font-sans font-bold text-[#2C1E12]">{trackingData.provider}</span></div>
                      <div className="max-w-full break-all">
                        Waybill (AWB):{' '}
                        <a 
                          href={`https://shiprocket.co/tracking/${trackingData.awb}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="font-bold text-red-950 underline hover:text-red-900 select-all bg-zinc-200/50 px-1.5 py-0.5 rounded-xs transition inline-block break-all"
                        >
                          {trackingData.awb}
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* POLICY CONTROLS */}
                <div className="flex flex-wrap items-center justify-between mt-2 pt-4 border-t border-zinc-100 gap-4 w-full">
                  <div className="flex flex-col items-start">
                    <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 border rounded-xs ${
                      order.status === 'Delivered' ? 'border-green-600 text-green-700 bg-green-50/50' :
                      order.status && order.status.includes('Requested') ? 'border-amber-600 text-amber-700 bg-amber-50/50' : 'border-[#2C1E12]/30 text-[#2C1E12]'
                    }`}>{order.status || 'Processing'}</span>
                    {!withinThreeDays && order.status === 'Delivered' && (
                      <span className="text-[8px] text-red-800 font-mono mt-1.5 uppercase tracking-wide">⚠️ 3-day policy window elapsed.</span>
                    )}
                  </div>

                  {order.status === 'Delivered' && withinThreeDays && (
                    <div className="flex flex-wrap gap-2.5">
                      <button onClick={() => { setActiveOrderId(order._id); setRequestType('Return'); }} className="text-[9px] font-black uppercase tracking-widest bg-zinc-100 text-[#2C1E12] border border-[#2C1E12]/20 px-3.5 py-2 hover:bg-zinc-200 transition rounded-sm flex items-center gap-1 cursor-pointer">
                        <Undo2 size={10} /> File Return
                      </button>
                      <button onClick={() => { setActiveOrderId(order._id); setRequestType('Exchange'); }} className="text-[9px] font-black uppercase tracking-widest bg-red-950 text-[#F5F5DC] px-3.5 py-2 hover:bg-red-900 transition rounded-sm shadow-sm flex items-center gap-1 cursor-pointer border-none">
                        <RefreshCw size={10} /> Request Exchange
                      </button>
                    </div>
                  )}
                </div>

              </div>
            );
          })}
        </div>
      )}

      {/* POLICY MODAL */}
      {activeOrderId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-[200] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#FAF9F6] border border-[#E5D3B3] max-w-md w-full p-6 sm:p-8 my-8 text-left rounded-sm shadow-2xl animate-fade-in font-sans max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif text-md text-[#2C1E12] uppercase tracking-wide mb-1 flex items-center gap-2">
              <AlertCircle size={16} className="text-red-900 animate-pulse" /> {requestType} Request Portal
            </h3>
            <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-4">Modische Kleding's Policy Validation Desk</p>
            
            <div className="bg-[#2C1E12]/5 border border-[#2C1E12]/10 p-3 rounded-xs mb-5 text-[9px] text-[#664229] space-y-1 font-sans">
              <p className="font-bold uppercase tracking-wider">🔒 Policy Clauses:</p>
              <p>• Items qualify for replacement or credit for fit or style preference within 3 days.</p>
              <p className="text-red-950 font-bold">• For defects or incorrect items, please upload clear inspection photos below.</p>
            </div>

            <label className="text-[8px] uppercase font-black text-[#664229] tracking-wider block mb-1">Reason for {requestType}</label>
            <select 
              value={selectedReason} 
              onChange={(e) => setSelectedReason(e.target.value)} 
              className="w-full bg-white border border-zinc-200 text-xs p-3 mb-4 focus:outline-none focus:border-[#2C1E12] rounded-xs font-sans text-[#2C1E12]"
            >
              <option value="">-- Select Policy Claim Condition --</option>
              <option value="I did not like the fit">I did not like the fit / Sizing choice</option>
              <option value="I did not like the quality">I did not like the garment quality</option>
              <option value="Changed my mind">Changed my mind / Alternative choice</option>
              <option value="Wrong product delivered">Wrong product delivered (Requires proof)</option>
              <option value="Damaged / defective item received">Damaged / defective item received (Requires proof)</option>
              <option value="Missing items or quantity mismatch">Missing items or quantity mismatch (Requires proof)</option>
            </select>

            <div className="mb-5">
              <label className="text-[8px] uppercase font-black text-[#664229] tracking-wider block mb-1">
                Garment Inspection Records ({uploadedImages.length}/6 Images Attached)
              </label>
              
              {uploadedImages.length < 6 && (
                <label className="w-full h-16 border border-dashed border-zinc-300 rounded-xs flex flex-col items-center justify-center bg-white cursor-pointer hover:bg-zinc-50/50 transition mb-3">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wide text-[#664229]">
                    <Upload size={12} /> Click to Upload Images
                  </div>
                  <input 
                    type="file" 
                    multiple 
                    accept="image/*" 
                    onChange={handleImageUploadChange} 
                    className="hidden" 
                  />
                </label>
              )}

              {uploadedImages.length > 0 && (
                <div className="grid grid-cols-6 gap-2 bg-zinc-100 p-2 rounded-xs border border-zinc-200">
                  {uploadedImages.map((imgSrc, index) => (
                    <div key={index} className="w-full aspect-[3/4] relative bg-white border border-zinc-200 rounded-xs overflow-hidden group">
                      <img src={imgSrc} alt="" className="w-full h-full object-cover" />
                      <button 
                        type="button" 
                        onClick={() => removeUploadedImage(index)} 
                        className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition border-none cursor-pointer text-white"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label className="text-[8px] uppercase font-black text-[#664229] tracking-wider block mb-1">Additional Specifications</label>
            <textarea 
              value={customNotes} 
              onChange={(e) => setCustomNotes(e.target.value)} 
              placeholder="Provide specific notes regarding defects or sizing requirements..." 
              className="w-full bg-white border border-zinc-200 text-xs p-3.5 min-h-[80px] focus:outline-none focus:border-[#2C1E12] font-sans rounded-xs resize-none mb-2" 
            />
            
            <div className="flex space-x-3 mt-6">
              <button 
                onClick={() => { setActiveOrderId(null); setSelectedReason(''); setCustomNotes(''); setUploadedImages([]); }} 
                className="flex-1 py-3 border border-zinc-200 text-[10px] uppercase tracking-wider font-black text-zinc-500 hover:bg-zinc-50 transition cursor-pointer bg-transparent"
              >
                Cancel
              </button>
              <button 
                onClick={handlePolicyActionSubmit} 
                className="flex-1 py-3 bg-red-950 text-[#F5F5DC] text-[10px] uppercase tracking-wider font-black hover:bg-red-900 transition shadow-md border-none cursor-pointer"
              >
                File {requestType}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistoryPage;